import { Component, computed, inject, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { MaterialModule } from '../../../../../shared/material.module';
import { TabToolbarComponent } from '../tab-toolbar/tab-toolbar.component';
import { KeywordInputComponent } from '../keyword-input/keyword-input.component';
import { HasUnsavedChanges } from '../../../../../guards/unsaved-changes.guard';
import { ConfirmationDialogService } from '../../../../../services/confirmation-dialog.service';
import { Helpers } from '../../../../../utilities/helpers';
import { CompanyCommitteesService } from '../../../../../services/companies/company-committees.service';
import {
  CommitteesResponseDto,
  CommitteeBoardMemberDto,
  CommitteesUpsertRequest,
  CommitteeBoardMemberUpsertDto
} from '../../../../../models/api/companyCommitteesDto';
import { ApiErrorResponse } from '../../../../../models/api/apiErrorResponse';
import { ErrorCodes } from '../../../../../models/api/errorCodes';
import { MEMBERS_DROPPED_MESSAGE, membersWereDropped } from '../../../../../models/api/yearlyResource';
import { selectTabYear, selectDefaultYear } from '../../../../../store/selectors/company-form.selectors';
import { setTabYear } from '../../../../../store/actions/company-form.actions';
import { NumberFormatDirective } from '../../../../../directives/number-format.directive';
import { CompanyKeywordService } from '../../../../../services/companies/company-keyword.service';
import { CompanyKeywordDto, CompanyKeywordUpdateRequest } from '../../../../../models/api/companyKeywordDto';
import { KeywordType } from '../../../../../models/enums/keywordType.enum';
import { buildKeywordRequests } from '../../../../../utilities/keyword-helpers';
import { saveTabThenKeywords } from '../../../../../utilities/tab-save';
import { serverErrorMessage } from '../../../../../utilities/api-error';
import { Observable, of } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, Sort } from '@angular/material/sort';
import { CurrentUserState } from '../../../../../state/current-user.state';
import { ReadOnlyTabDirective } from '../../../../../directives/read-only-tab.directive';
import { AppUserCompanyAccessType } from '../../../../../models/enums/appUserCompanyAccessType.enum';

interface CommitteeRoleOption {
  value: string;
  label: string;
}

/** Definition for each committee column in the members table */
interface CommitteeColumnDef {
  /** Column ID used in displayedColumns */
  id: string;
  /** Short header label */
  header: string;
  /** formControlName within the row group */
  formControl: string;
  /** Which role options set to use */
  optionsType: 'audit' | 'standard';
}

@Component({
  selector: 'app-committees-tab',
  imports: [
    ReactiveFormsModule,
    MaterialModule,
    NgxSpinnerModule,
    TabToolbarComponent,
    KeywordInputComponent,
    NumberFormatDirective,
    ReadOnlyTabDirective,
  ],
  templateUrl: './committees-tab.component.html',
  styleUrl: './committees-tab.component.scss'
})
export class CommitteesTabComponent implements OnInit, OnChanges, HasUnsavedChanges {
  @Input() keywords: CompanyKeywordDto[] = [];
  @Output() keywordsUpdated = new EventEmitter<CompanyKeywordDto[]>();

  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private committeesService = inject(CompanyCommitteesService);
  private companyKeywordService = inject(CompanyKeywordService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private toast = inject(HotToastService);
  private spinnerService = inject(NgxSpinnerService);
  private currentUserState = inject(CurrentUserState);

  /** Tab held read-only: the form is disabled by ReadOnlyTabDirective and row actions are hidden. */
  readonly isReadOnly = computed(() =>
    this.currentUserState.isCompanyTabReadOnly(AppUserCompanyAccessType.Committees));

  selectedYear: string = this.currentUserState.defaultMeasurementYear();
  companyId: string | null = null;
  showYearWarning = false;
  isCommitteeCarriedForward = false;
  showKeywords = true;
  companyBoardType: number = 0; // 0 = Unitary, non-zero = Dual
  otherMeasurement: string = '';
  otherData: CommitteesResponseDto | null = null;

  /** Audit committee role options: (empty), OK, CH, FE, CH-FE */
  auditRoleOptions: CommitteeRoleOption[] = [
    { value: '', label: '' },
    { value: '1', label: 'OK' },
    { value: '2', label: 'CH' },
    { value: '3', label: 'FE' },
    { value: '4', label: 'CH-FE' }
  ];

  /** Standard committee role options: (empty), OK, CH */
  standardRoleOptions: CommitteeRoleOption[] = [
    { value: '', label: '' },
    { value: '1', label: 'OK' },
    { value: '2', label: 'CH' }
  ];

  /** Committee column definitions for the members table */
  committeeColumns: CommitteeColumnDef[] = [
    { id: 'audit', header: 'Audit', formControl: 'auditCommitteeRole', optionsType: 'audit' },
    { id: 'rem', header: 'Rem.', formControl: 'remCommitteeRole', optionsType: 'standard' },
    { id: 'nom', header: 'Nom.', formControl: 'nomCommitteeRole', optionsType: 'standard' },
    { id: 'cg', header: 'CG', formControl: 'cgCommitteeRole', optionsType: 'standard' },
    { id: 'risk', header: 'Risk', formControl: 'riskCommitteeRole', optionsType: 'standard' },
    { id: 'finance', header: 'Fin.', formControl: 'financeCommitteeRole', optionsType: 'standard' },
    { id: 'investment', header: 'Inv.', formControl: 'investmentCommitteeRole', optionsType: 'standard' },
    { id: 'csr', header: 'ESG', formControl: 'csrCommitteeRole', optionsType: 'standard' },
    { id: 'executive', header: 'Exec.', formControl: 'executiveCommitteeRole', optionsType: 'standard' },
    { id: 'hr', header: 'HR', formControl: 'hrCommitteeRole', optionsType: 'standard' }
  ];

  /** Unitary board or Supervisory board (dual) */
  boardDataSource = new MatTableDataSource<CommitteeBoardMemberDto>([]);
  /** Management board (dual only) */
  managementDataSource = new MatTableDataSource<CommitteeBoardMemberDto>([]);

  @ViewChild('boardSort') boardSort!: MatSort;
  @ViewChild('managementSort') managementSort!: MatSort;

  displayedColumns: string[] = [
    'index', 'name',
    ...this.committeeColumns.map(c => c.id)
  ];

  form: FormGroup = this.fb.group({
    rows: this.fb.array([]),
    managementRows: this.fb.array([]),
    auditCommitteeMeetings: [null],
    remCommitteeMeetings: [null],
    nomCommitteeMeetings: [null],
    cgCommitteeMeetings: [null],
    riskCommitteeMeetings: [null],
    financeCommitteeMeetings: [null],
    investmentCommitteeMeetings: [null],
    csrCommitteeMeetings: [null],
    executiveCommitteeMeetings: [null],
    hrCommitteeMeetings: [null],
    audit: [false],
    rem: [false],
    nom: [false],
    comments: [''],
    keywords: this.fb.group({
      meetings: ['']
    })
  });

  get rowsArray(): FormArray {
    return this.form.get('rows') as FormArray;
  }

  get managementRowsArray(): FormArray {
    return this.form.get('managementRows') as FormArray;
  }

  get hasChanges(): boolean {
    return this.form.dirty;
  }

  hasUnsavedChanges(): boolean {
    return this.hasChanges;
  }

  ngOnInit(): void {
    this.companyId = this.route.snapshot.paramMap.get('id') ?? null;

    const storedYear = this.store.selectSignal(selectTabYear('committees'))();
    const defaultYear = this.store.selectSignal(selectDefaultYear)();
    if (storedYear) {
      this.selectedYear = storedYear;
    } else if (defaultYear) {
      this.selectedYear = defaultYear;
      this.store.dispatch(setTabYear({ tabId: 'committees', year: defaultYear }));
    } else {
      this.store.dispatch(setTabYear({ tabId: 'committees', year: this.selectedYear }));
    }

    this.patchKeywords();
    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['keywords'] && !changes['keywords'].firstChange) {
      this.patchKeywords();
    }
  }

  private patchKeywords(): void {
    const meetings = this.keywords.find(k => k.type === KeywordType.CommitteeMeetings);
    this.form.get('keywords')!.patchValue({
      meetings: meetings?.value ?? ''
    }, { emitEvent: false });
    this.form.markAsPristine();
  }

  private getSaveDescription(): string {
    const hasKeywordChanges = this.form.get('keywords')!.dirty;
    const hasCommitteeChanges = this.form.dirty && !hasKeywordChanges ? true :
      this.rowsArray.dirty || this.managementRowsArray.dirty ||
      ['auditCommitteeMeetings', 'remCommitteeMeetings', 'nomCommitteeMeetings',
       'cgCommitteeMeetings', 'riskCommitteeMeetings', 'financeCommitteeMeetings',
       'investmentCommitteeMeetings', 'csrCommitteeMeetings', 'executiveCommitteeMeetings',
       'hrCommitteeMeetings', 'audit', 'rem', 'nom', 'comments'
      ].some(f => this.form.get(f)?.dirty);

    if (hasCommitteeChanges && hasKeywordChanges) return 'committees data and keywords';
    if (hasKeywordChanges) return 'keywords';
    return 'committees data';
  }

  onYearChange(year: string): void {
    if (this.hasChanges) {
      this.confirmationDialog.openConfirmationDialog(
        'Unsaved Changes',
        'You have unsaved changes. Are you sure you want to switch measurement?',
        'Switch',
        'Stay',
        'swap_horiz',
        'edit'
      ).subscribe(confirmed => {
        if (confirmed) {
          this.applyYearChange(year);
        } else {
          const current = this.selectedYear;
          this.selectedYear = '';
          setTimeout(() => this.selectedYear = current);
        }
      });
    } else {
      this.applyYearChange(year);
    }
  }

  onOtherMeasurementChange(year: string): void {
    this.otherMeasurement = year;
    if (!year || !this.companyId) {
      this.otherData = null;
      return;
    }
    this.loadOtherMeasurementData();
  }

  formatNumber(value: number | null | undefined): string {
    if (value == null) return '';
    return value.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  onSave(): void {
    if (!this.companyId) return;
    this.performSave();
  }

  getOptionsForColumn(col: CommitteeColumnDef): CommitteeRoleOption[] {
    return col.optionsType === 'audit' ? this.auditRoleOptions : this.standardRoleOptions;
  }

  getRoleLabel(col: CommitteeColumnDef, value: any): string {
    if (value == null || value === '') return '';
    const options = this.getOptionsForColumn(col);
    return options.find(o => o.value === String(value))?.label ?? '';
  }

  private loadOtherMeasurementData(): void {
    if (!this.companyId || !this.otherMeasurement) return;
    this.committeesService.getAll(this.companyId, parseInt(this.otherMeasurement)).subscribe({
      next: resource => {
        this.otherData = resource.state === 'present' ? resource.data : null;
      },
      error: () => {
        this.otherData = null;
      }
    });
  }

  private applyYearChange(year: string): void {
    this.selectedYear = year;
    this.store.dispatch(setTabYear({ tabId: 'committees', year }));
    this.loadData();
  }

  private loadData(): void {
    if (!this.companyId) return;

    this.spinnerService.show();
    this.committeesService.getAll(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: resource => {
        const response = resource.state === 'present'
          ? resource.data
          : (resource.state === 'carried-forward' ? resource.carriedForward?.values : null);
        this.isCommitteeCarriedForward = resource.state === 'carried-forward';
        this.showYearWarning = this.isCommitteeCarriedForward || resource.state === 'absent';

        // Members come from context — populated in every state, including 'absent', so
        // the table is always rendered alongside whatever committee-level data is (or isn't) there.
        const members = resource.context?.boardMembers ?? [];
        const boardType = resource.context?.boardType ?? 0;
        this.populateMemberTables(members, boardType);

        if (response) {
          this.populateCommitteeFields(response);
        } else {
          this.clearCommitteeFields();
        }
        this.spinnerService.hide();
      },
      error: (error: any) => {
        this.spinnerService.hide();
        const apiError = error.error as ApiErrorResponse;

        if (apiError?.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
          this.resetForm();
        } else {
          this.toast.error('Error loading committees data');
        }

        console.error('Committees data error:', error);
      }
    });
  }

  private populateMemberTables(members: CommitteeBoardMemberDto[], boardType: number): void {
    this.companyBoardType = boardType;

    if (boardType !== 0) {
      // Dual: supervisory (type 1) and management (type 2)
      const supervisory = members.filter(m => m.boardType === 1);
      const management = members.filter(m => m.boardType === 2);
      this.boardDataSource.data = supervisory;
      this.managementDataSource.data = management;
      this.buildFormArrays(supervisory, management);
    } else {
      // Unitary: all in one table
      this.boardDataSource.data = members;
      this.managementDataSource.data = [];
      this.buildFormArrays(members, []);
    }

    this.onBoardSort(this.currentSort(this.boardSort));
    if (boardType !== 0) {
      this.onManagementSort(this.currentSort(this.managementSort));
    }
  }

  private populateCommitteeFields(response: CommitteesResponseDto): void {
    this.form.patchValue({
      auditCommitteeMeetings: response.auditCommitteeMeetings ?? null,
      remCommitteeMeetings: response.remCommitteeMeetings ?? null,
      nomCommitteeMeetings: response.nomCommitteeMeetings ?? null,
      cgCommitteeMeetings: response.cgCommitteeMeetings ?? null,
      riskCommitteeMeetings: response.riskCommitteeMeetings ?? null,
      financeCommitteeMeetings: response.financeCommitteeMeetings ?? null,
      investmentCommitteeMeetings: response.investmentCommitteeMeetings ?? null,
      csrCommitteeMeetings: response.csrCommitteeMeetings ?? null,
      executiveCommitteeMeetings: response.executiveCommitteeMeetings ?? null,
      hrCommitteeMeetings: response.hrCommitteeMeetings ?? null,
      audit: response.audit ?? false,
      rem: response.rem ?? false,
      nom: response.nom ?? false,
      comments: response.comments ?? ''
    });
    this.form.markAsPristine();
  }

  private clearCommitteeFields(): void {
    this.form.patchValue({
      auditCommitteeMeetings: null,
      remCommitteeMeetings: null,
      nomCommitteeMeetings: null,
      cgCommitteeMeetings: null,
      riskCommitteeMeetings: null,
      financeCommitteeMeetings: null,
      investmentCommitteeMeetings: null,
      csrCommitteeMeetings: null,
      executiveCommitteeMeetings: null,
      hrCommitteeMeetings: null,
      audit: false,
      rem: false,
      nom: false,
      comments: ''
    });
    this.form.markAsPristine();
  }

  private buildFormArrays(boardRows: CommitteeBoardMemberDto[], managementRows: CommitteeBoardMemberDto[]): void {
    this.form.setControl('rows', this.fb.array(
      boardRows.map(m => this.createMemberRowGroup(m))
    ));
    this.form.setControl('managementRows', this.fb.array(
      managementRows.map(m => this.createMemberRowGroup(m))
    ));
  }

  private createMemberRowGroup(member: CommitteeBoardMemberDto): FormGroup {
    return this.fb.group({
      detailsId: [member.detailsId],
      boardMemberId: [member.boardMemberId],
      directorId: [member.directorId],
      auditCommitteeRole: [member.auditCommitteeRole ?? ''],
      remCommitteeRole: [member.remCommitteeRole ?? ''],
      nomCommitteeRole: [member.nomCommitteeRole ?? ''],
      cgCommitteeRole: [member.cgCommitteeRole ?? ''],
      riskCommitteeRole: [member.riskCommitteeRole ?? ''],
      financeCommitteeRole: [member.financeCommitteeRole ?? ''],
      investmentCommitteeRole: [member.investmentCommitteeRole ?? ''],
      csrCommitteeRole: [member.csrCommitteeRole ?? ''],
      executiveCommitteeRole: [member.executiveCommitteeRole ?? ''],
      hrCommitteeRole: [member.hrCommitteeRole ?? '']
    });
  }

  toggleRowHighlight(row: any): void {
    row.highlighted = !row.highlighted;
  }

  onBoardSort(sort: Sort): void {
    this.sortTable(sort, this.boardDataSource, this.rowsArray, 'rows');
  }

  onManagementSort(sort: Sort): void {
    this.sortTable(sort, this.managementDataSource, this.managementRowsArray, 'managementRows');
  }

  /**
   * The table's current sort, taken from its MatSort header so that rebuilding the
   * rows after a reload (e.g. a save) keeps the column the user picked instead of
   * snapping the data back to name/asc — which left the sort arrow pointing at one
   * column while the rows were ordered by another. Falls back to name/asc before the
   * header has rendered (first load).
   */
  private currentSort(sort: MatSort | undefined): Sort {
    return sort?.active && sort.direction
      ? { active: sort.active, direction: sort.direction }
      : { active: 'name', direction: 'asc' };
  }

  private sortTable(sort: Sort, dataSource: MatTableDataSource<CommitteeBoardMemberDto>, formArray: FormArray, formArrayName: string): void {
    if (!sort.active || sort.direction === '') return;

    const dir = sort.direction === 'asc' ? 1 : -1;

    // Pair each row with its FormGroup, snapshotting current form values into the
    // data object so sorting sees in-progress edits.
    const paired = dataSource.data.map((row, i) => {
      const group = formArray.at(i) as FormGroup;
      return { merged: { ...row, ...group.value }, group };
    });

    paired.sort((pa, pb) => {
      const a = pa.merged;
      const b = pb.merged;
      let valA: any;
      let valB: any;

      if (sort.active === 'name') {
        valA = a.directorFullName ?? '';
        valB = b.directorFullName ?? '';
        return dir * valA.localeCompare(valB);
      }

      // Committee columns — sort by role value (numeric string)
      const col = this.committeeColumns.find(c => c.id === sort.active);
      if (col) {
        valA = parseInt(a[col.formControl]) || 0;
        valB = parseInt(b[col.formControl]) || 0;
        return dir * (valA - valB);
      }

      return 0;
    });

    // Reuse the existing FormGroup instances so per-row dirty state, values, and
    // valueChanges subscriptions survive the sort. Recreating the groups would reset
    // their dirty flags, and performSave() only sends rows whose group is dirty — so
    // any edit made before a sort would be silently dropped on save.
    dataSource.data = paired.map(p => p.merged);
    this.form.setControl(formArrayName, this.fb.array(paired.map(p => p.group)));
  }

  private resetForm(): void {
    this.boardDataSource.data = [];
    this.managementDataSource.data = [];
    this.form.setControl('rows', this.fb.array([]));
    this.form.setControl('managementRows', this.fb.array([]));
    this.form.patchValue({
      auditCommitteeMeetings: null,
      remCommitteeMeetings: null,
      nomCommitteeMeetings: null,
      cgCommitteeMeetings: null,
      riskCommitteeMeetings: null,
      financeCommitteeMeetings: null,
      investmentCommitteeMeetings: null,
      csrCommitteeMeetings: null,
      executiveCommitteeMeetings: null,
      hrCommitteeMeetings: null,
      audit: false,
      rem: false,
      nom: false,
      comments: ''
    });
    this.form.markAsPristine();
  }

  private performSave(): void {
    if (!this.companyId) return;

    const formVal = this.form.value;

    // Member details have open-ended ValidTo and naturally extend across years, so a
    // carry-forward view doesn't require resending unchanged rows — only dirty ones.
    const boardMembers: CommitteeBoardMemberUpsertDto[] = [];

    this.rowsArray.controls.forEach((group) => {
      if (group.dirty) {
        boardMembers.push(this.buildMemberUpsertDto(group.value));
      }
    });

    this.managementRowsArray.controls.forEach((group) => {
      if (group.dirty) {
        boardMembers.push(this.buildMemberUpsertDto(group.value));
      }
    });

    // Per-year CompanyCommittee fields are materialized for the year on carry-forward saves
    // so the year gets its own record.
    const meetingFields = [
      'auditCommitteeMeetings', 'remCommitteeMeetings', 'nomCommitteeMeetings',
      'cgCommitteeMeetings', 'riskCommitteeMeetings', 'financeCommitteeMeetings',
      'investmentCommitteeMeetings', 'csrCommitteeMeetings', 'executiveCommitteeMeetings',
      'hrCommitteeMeetings', 'audit', 'rem', 'nom', 'comments'
    ];
    const topLevelDirty = this.isCommitteeCarriedForward || meetingFields.some(field => this.form.get(field)?.dirty);

    // Build keyword upsert requests
    const keywordsValue = this.form.get('keywords')!.value;
    const keywordRequests: CompanyKeywordUpdateRequest[] = buildKeywordRequests(
      keywordsValue,
      [{ formField: 'meetings', type: KeywordType.CommitteeMeetings }],
      this.keywords
    );

    const hasCommitteeData = boardMembers.length > 0 || topLevelDirty;
    const hasKeywordData = keywordRequests.length > 0 || this.keywords.some(
      k => k.type === KeywordType.CommitteeMeetings
    );

    if (!hasCommitteeData && !hasKeywordData) {
      this.toast.info('No changes to save');
      return;
    }

    const description = this.getSaveDescription();

    const request: CommitteesUpsertRequest = {
      year: parseInt(this.selectedYear),
      auditCommitteeMeetings: formVal.auditCommitteeMeetings,
      remCommitteeMeetings: formVal.remCommitteeMeetings,
      nomCommitteeMeetings: formVal.nomCommitteeMeetings,
      cgCommitteeMeetings: formVal.cgCommitteeMeetings,
      riskCommitteeMeetings: formVal.riskCommitteeMeetings,
      financeCommitteeMeetings: formVal.financeCommitteeMeetings,
      investmentCommitteeMeetings: formVal.investmentCommitteeMeetings,
      csrCommitteeMeetings: formVal.csrCommitteeMeetings,
      executiveCommitteeMeetings: formVal.executiveCommitteeMeetings,
      hrCommitteeMeetings: formVal.hrCommitteeMeetings,
      audit: formVal.audit,
      rem: formVal.rem,
      nom: formVal.nom,
      comments: formVal.comments,
      boardMembers
    };

    this.spinnerService.show();

    const saveCalls: Observable<any>[] = [];
    saveCalls.push(hasCommitteeData
      ? this.committeesService.upsert(this.companyId!, request)
      : of(null));
    saveTabThenKeywords(saveCalls, this.companyKeywordService.upsert(this.companyId!, keywordRequests)).subscribe({
      next: ([committeeResult, keywordsResult]) => {
        if (hasCommitteeData) {
          this.showYearWarning = false;
        }
        this.keywordsUpdated.emit(keywordsResult as CompanyKeywordDto[]);
        this.form.markAsPristine();
        this.spinnerService.hide();
        this.toast.success(`Saved ${description} successfully`);
        // The server refuses rows for members the year's roster no longer holds — someone was
        // removed on the Board tab while this table was still showing the old roster.
        if (membersWereDropped(committeeResult)) {
          this.toast.warning(MEMBERS_DROPPED_MESSAGE);
        }
        if (hasCommitteeData) {
          this.loadData();
        }
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error(serverErrorMessage(err, `Failed to save ${description}`));
        console.error('Save error:', err);
      }
    });
  }

  private buildMemberUpsertDto(val: any): CommitteeBoardMemberUpsertDto {
    return {
      detailsId: val.detailsId || undefined,
      boardMemberId: val.boardMemberId,
      directorId: val.directorId || undefined,
      auditCommitteeRole: val.auditCommitteeRole !== '' ? val.auditCommitteeRole : undefined,
      remCommitteeRole: val.remCommitteeRole !== '' ? val.remCommitteeRole : undefined,
      nomCommitteeRole: val.nomCommitteeRole !== '' ? val.nomCommitteeRole : undefined,
      cgCommitteeRole: val.cgCommitteeRole !== '' ? val.cgCommitteeRole : undefined,
      riskCommitteeRole: val.riskCommitteeRole !== '' ? val.riskCommitteeRole : undefined,
      financeCommitteeRole: val.financeCommitteeRole !== '' ? val.financeCommitteeRole : undefined,
      investmentCommitteeRole: val.investmentCommitteeRole !== '' ? val.investmentCommitteeRole : undefined,
      csrCommitteeRole: val.csrCommitteeRole !== '' ? val.csrCommitteeRole : undefined,
      executiveCommitteeRole: val.executiveCommitteeRole !== '' ? val.executiveCommitteeRole : undefined,
      hrCommitteeRole: val.hrCommitteeRole !== '' ? val.hrCommitteeRole : undefined
    };
  }
}
